import { useFormikContext } from 'formik';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import { Autocomplete, TextField, useTheme } from '@mui/material';
import _ from 'lodash';
import axios, { CancelTokenSource } from 'axios';
import { DEBOUNCE_TIMEOUT_MS } from '../../../utils/constants';
import { supplierCarService } from '@api/services/services';
import { SupplierCarDto } from '@api/models/suppliers/SupplierCarDto';
import { SupplierDto } from '@api/models/suppliers/SupplierDto';
import { normalizeSpz } from '../../../utils/spzNormalization';
import QcsAlert from '../basic/QcsAlert';
import { useTranslation } from 'react-i18next';
import { VisitationFormContext } from '../../visitation/VisitationAddModalPin';

interface Props {
  className?: string;
  fullWidth?: boolean;
  required?: boolean;
  name: string;
  label?: React.ReactNode;
  disabled?: boolean;
  autoFocus?: boolean;
}

export const SpzAutocomplete: FC<Props> = ({ className, fullWidth, required, name, label, disabled, autoFocus }) => {
  const { values, errors, touched, submitCount, handleBlur, setFieldValue } = useFormikContext<any>();
  const [options, setOptions] = useState<SupplierCarDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const dataCancelToken = useRef<CancelTokenSource | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const duplicationsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [spzDuplications, setSpzDuplications] = useState<SupplierCarDto[]>([]);
  const { t } = useTranslation();

  const visitationContext = useContext(VisitationFormContext);

  const value = _.get(values, name);
  const wasTouched = !!submitCount || !!_.get(touched, name);
  const errorText = _.get(errors, name) as string;
  const hasError = !!errorText;

  const theme = useTheme();

  useEffect(() => {
    return () => {
      // Debounces autocomplete search to avoid excessive API calls
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      dataCancelToken.current?.cancel();
    };
  }, []);

  useEffect(() => {
    return () => {
      // Debounces autocomplete search to avoid excessive API calls
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      dataCancelToken.current?.cancel();
    };
  }, [values.pin]);

  useEffect(() => {
    // Clear any existing timeout to debounce the API call
    if (duplicationsTimeoutRef.current) {
      clearTimeout(duplicationsTimeoutRef.current);
    }

    // Set new timeout for debounced fetch
    duplicationsTimeoutRef.current = setTimeout(() => {
      const fetchSpzDuplications = async () => {
        if (!values.spz || !values.supplier) {
          setSpzDuplications([]);
          return;
        }
        const cars = await getSupplierCarsBySpz();

        const currentSupplierCar = cars.find((it) => it.supplier!.id === values.supplier.id);
        if (currentSupplierCar && currentSupplierCar.lastEnteredBy && !values.supplierEmployee) {
          setFieldValue('supplierEmployee', currentSupplierCar.lastEnteredBy);
        }

        // show the duplications only if user selected a supplier which doesn't already have car with this spz
        if (cars.some((it) => it.supplier?.id === values.supplier.id)) {
          setSpzDuplications([]);
          return;
        }
        const carDuplications = cars.filter((it) => it.supplier?.id !== values.supplier.id && it.spz?.toLowerCase() === values.spz?.toLowerCase());
        setSpzDuplications(carDuplications);
      };

      fetchSpzDuplications();
    }, DEBOUNCE_TIMEOUT_MS);

    // Cleanup function to clear timeout on unmount or dependency change
    return () => {
      if (duplicationsTimeoutRef.current) {
        clearTimeout(duplicationsTimeoutRef.current);
      }
    };
  }, [values.spz, values.supplier]);

  async function getSupplierCarsBySpz(spz?: string) {
    const carsWithSpz = await supplierCarService.getSupplierCars({ spz: spz ?? values.spz });
    return carsWithSpz.data.content;
  }

  useEffect(() => {
    // Set new timeout for debounced fetch
    const setSupplierEmployee = async () => {
      if (!values.spz || !values.supplier) return;
      const cars = await getSupplierCarsBySpz();

      const currentSupplierCar = cars.find((it) => it.supplier!.id === values.supplier.id);
      if (currentSupplierCar && currentSupplierCar.lastEnteredBy && !values.supplierEmployee) {
        setFieldValue('supplierEmployee', currentSupplierCar.lastEnteredBy);
      }
    };

    setSupplierEmployee();
  }, [values.spz, values.supplier]);

  // Update hasValidCar in context when SPZ changes
  useEffect(() => {
    const checkCarValidity = async () => {
      if (!visitationContext) return;

      if (!values.spz) {
        visitationContext.setHasValidCar(false);
        return;
      }

      try {
        const cars = await getSupplierCarsBySpz();
        visitationContext.setHasValidCar(cars.length > 0);
      } catch {
        visitationContext.setHasValidCar(false);
      }
    };

    checkCarValidity();
  }, [values.spz, visitationContext]);

  const loadOptions = async (searchValue: string) => {
    if (!searchValue) {
      setOptions([]);
      return;
    }

    setLoading(true);
    const cars = await getSupplierCarsBySpz(searchValue);

    // Remove duplicate SPZ values - keep only first occurrence of each unique SPZ
    setOptions(cars.filter((car, index, self) => index === self.findIndex((c) => c.spz === car.spz)));
    setLoading(false);
  };

  // Debounces autocomplete search to avoid excessive API calls
  const handleInputChange = (_event: any, newInputValue: string) => {
    setInputValue(newInputValue);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    dataCancelToken.current?.cancel();

    if (!newInputValue) {
      setOptions([]);
      return;
    }

    timeoutRef.current = setTimeout(() => {
      dataCancelToken.current?.cancel();
      dataCancelToken.current = axios.CancelToken.source();
      loadOptions(newInputValue);
    }, DEBOUNCE_TIMEOUT_MS);
  };

  // If user selected an option from the list
  const handleChange = async (_event: any, newValue: string | SupplierCarDto | null) => {
    if (!newValue) {
      setFieldValue(name, '');
      clearSupplierFields();
      return;
    }

    switch (typeof newValue) {
      // If user manually typed something and clicked Enter
      case 'string': {
        setFieldValue(name, newValue);
        visitationContext?.setValidSuppliers(null);
        break;
      }
      case 'object': {
        setFieldValue(name, newValue.spz);
        const cars = await getSupplierCarsBySpz(newValue.spz);
        const suppliers = cars.map((car) => car.supplier).filter((s): s is SupplierDto => s !== undefined);

        if (suppliers.length === 1) {
          setFieldValue('supplier', newValue.supplier);
          visitationContext?.setValidSuppliers(null);
          if (newValue.lastEnteredBy) setFieldValue('supplierEmployee', newValue.lastEnteredBy);
        }

        if (suppliers.length > 1) visitationContext?.setValidSuppliers(suppliers); // Multiple suppliers have cars with same SPZ - allow selection among them
      }
    }
  };

  const clearSupplierFields = async () => {
    await setFieldValue('supplier', null);
    await setFieldValue('supplierEmployee', null);
    visitationContext?.setValidSuppliers(null);
  };

  const handleSpzBlur = async () => {
    const currentValue = inputValue || _.get(values, name) || '';
    const normalized = normalizeSpz(currentValue);
    setFieldValue(name, normalized);
    if (!normalized) return;

    const cars = await getSupplierCarsBySpz(normalized);

    const carsWithExactSpz = cars.filter((it) => it.spz === normalized);
    if (carsWithExactSpz.length < 1) return;

    if (carsWithExactSpz.length === 1) {
      const car = carsWithExactSpz[0];
      clearSupplierFields().then(() => {
        setFieldValue('supplier', car.supplier);
        if (car.lastEnteredBy) setFieldValue('supplierEmployee', car.lastEnteredBy);
      });
      return;
    }

    const suppliers = cars.map((car) => car.supplier).filter((s): s is SupplierDto => s !== undefined);

    visitationContext?.setValidSuppliers(suppliers);
  };

  const onTextFieldChange = () => {
    clearSupplierFields();
  };

  return (
    <>
      <Autocomplete
        freeSolo
        className={className}
        fullWidth={fullWidth}
        options={options}
        loading={loading}
        value={value || ''}
        inputValue={inputValue || value || ''}
        onInputChange={handleInputChange}
        onChange={handleChange}
        onBlur={(e) => {
          handleBlur(e);
          handleSpzBlur();
        }}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option.spz || '';
        }}
        isOptionEqualToValue={(option, value) => {
          if (typeof value === 'string') return option.spz === value;
          return option.id === value.id;
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.spz}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            label={label}
            required={required}
            error={wasTouched && hasError}
            onChange={onTextFieldChange}
            helperText={wasTouched && errorText}
            autoFocus={autoFocus}
            sx={{
              '& .MuiInputBase-root': { backgroundColor: theme.palette.common.white },
              '& .MuiFormHelperText-root': { backgroundColor: 'transparent', marginLeft: 0, marginRight: 0 },
            }}
          />
        )}
        disabled={disabled}
      />
      {spzDuplications.length === 1 && (
        <QcsAlert severity="warning" sx={{ marginTop: '0.5rem' }}>
          {t('supplier.car.spzDuplication', { supplier: spzDuplications[0].supplier?.name ?? '' })}
        </QcsAlert>
      )}
      {spzDuplications.length > 1 && (
        <QcsAlert severity="warning" sx={{ marginTop: '0.5rem' }}>
          {t('supplier.car.spzDuplications', { suppliers: spzDuplications.map((it) => it.supplier?.name).join(', ') })}
        </QcsAlert>
      )}
    </>
  );
};
